import type {
  CommitFundingBatchResponse,
  CreateFundingBatchRequest,
  CreateFundingBatchResponse,
  GetFundingBatchResponse,
  ListFundingBatchesResponse,
  UpdateFundingBatchRequest,
  UpdateFundingBatchResponse,
  UpstreamFundingBatchRecord,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed React Query wrappers for the `/v1/ops/upstreams/:id/funding-batches`
 * + `/v1/ops/funding-batches/:id` surfaces (Epic #84 PR-P6).
 *
 * Query key conventions:
 *   - `['funding-batches', 'for-upstream', upstreamId]` — per-upstream list
 *   - `['funding-batches', 'single', batchId]` — single batch lookup
 *
 * Mutation invalidation:
 *   - `useCreateFundingBatch(upstreamId)` invalidates the per-upstream list
 *     only. The single-batch key is not invalidated because a freshly-
 *     created batch has a new id the consumer hasn't fetched yet.
 */

const UPSTREAM_BASE = '/v1/ops/upstreams';
const BATCH_BASE = '/v1/ops/funding-batches';

export function fundingBatchListQueryKey(
  upstreamId: string,
): readonly ['funding-batches', 'for-upstream', string] {
  return ['funding-batches', 'for-upstream', upstreamId] as const;
}

export function fundingBatchSingleQueryKey(
  batchId: string,
): readonly ['funding-batches', 'single', string] {
  return ['funding-batches', 'single', batchId] as const;
}

export async function listFundingBatches(
  upstreamId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<readonly UpstreamFundingBatchRecord[]> {
  const response = await opsApiRequest<ListFundingBatchesResponse>(
    `${UPSTREAM_BASE}/${encodeURIComponent(upstreamId)}/funding-batches`,
    { signal },
    fetchImpl,
  );
  return response.batches;
}

export async function getFundingBatch(
  batchId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamFundingBatchRecord> {
  const response = await opsApiRequest<GetFundingBatchResponse>(
    `${BATCH_BASE}/${encodeURIComponent(batchId)}`,
    { signal },
    fetchImpl,
  );
  return response.batch;
}

export async function createFundingBatch(
  upstreamId: string,
  body: CreateFundingBatchRequest,
  fetchImpl: typeof fetch,
): Promise<UpstreamFundingBatchRecord> {
  const response = await opsApiRequest<CreateFundingBatchResponse>(
    `${UPSTREAM_BASE}/${encodeURIComponent(upstreamId)}/funding-batches`,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.batch;
}

export function useFundingBatches(
  upstreamId: string,
): UseQueryResult<readonly UpstreamFundingBatchRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: fundingBatchListQueryKey(upstreamId),
    queryFn: ({ signal }) => listFundingBatches(upstreamId, fetchImpl, signal),
    enabled: upstreamId.length > 0,
  });
}

export function useCreateFundingBatch(
  upstreamId: string,
): UseMutationResult<UpstreamFundingBatchRecord, Error, CreateFundingBatchRequest> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createFundingBatch(upstreamId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: fundingBatchListQueryKey(upstreamId) });
    },
  });
}

export async function updateFundingBatch(
  batchId: string,
  body: UpdateFundingBatchRequest,
  fetchImpl: typeof fetch,
): Promise<UpstreamFundingBatchRecord> {
  const response = await opsApiRequest<UpdateFundingBatchResponse>(
    `${BATCH_BASE}/${encodeURIComponent(batchId)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
  return response.batch;
}

export async function commitFundingBatch(
  batchId: string,
  fetchImpl: typeof fetch,
): Promise<UpstreamFundingBatchRecord> {
  const response = await opsApiRequest<CommitFundingBatchResponse>(
    `${BATCH_BASE}/${encodeURIComponent(batchId)}/commit`,
    { method: 'POST' },
    fetchImpl,
  );
  return response.batch;
}

export function useUpdateFundingBatch(
  upstreamId: string,
): UseMutationResult<UpstreamFundingBatchRecord, Error, { batchId: string; body: UpdateFundingBatchRequest }> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, body }) => updateFundingBatch(batchId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: fundingBatchListQueryKey(upstreamId) });
    },
  });
}

export function useCommitFundingBatch(
  upstreamId: string,
): UseMutationResult<UpstreamFundingBatchRecord, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId) => commitFundingBatch(batchId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: fundingBatchListQueryKey(upstreamId) });
    },
  });
}
